import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Paper,
  Container,
  Stack,
  Typography,
  Chip,
  Button,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMapLocationDot,
  faTriangleExclamation,
  faLocationCrosshairs,
  faSatelliteDish,
} from "@fortawesome/free-solid-svg-icons";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import moment from "moment/moment";
import { isEmpty } from "lodash";

import { DataContext } from "../../../context/DataContext";
import { getToken } from "../../../api/Account";
import { getRouteAsync, getUsuariosCampoAsync } from "../../../api/Positions";
import { simpleMessage } from "../../../helpers/Helpers";
import {
  ahoraEnNicaragua,
  esIncidencia,
  estadoSenal,
  EVENTOS,
  formatearMinutos,
  haceCuanto,
  minutosDesde,
  RECORRIDO,
  REFRESCO_MS,
  ultimoConocido,
  CENTRO_POR_DEFECTO,
} from "../../../helpers/Seguimiento";
import {
  CentrarEn,
  icono,
  iconoAlerta,
  iconoGestor,
  iconoInicio,
} from "../../../components/MapaSeguimiento";
import NoData from "../../../components/NoData";

/**
 * Junta incidencias consecutivas en un solo corte. El telefono reporta una cada 15
 * minutos, y mostrarlas sueltas llenaria el mapa de marcadores repetidos en vez de decir
 * lo unico que importa: desde cuando hasta cuando estuvo sin registrar.
 */
const agruparCortes = (puntos) => {
  const cortes = [];

  puntos.forEach((punto) => {
    if (!esIncidencia(punto)) {
      return;
    }

    const ultimo = cortes[cortes.length - 1];
    const continua =
      ultimo !== undefined &&
      ultimo.kind === punto.kind &&
      ultimo.indiceFin === punto.indice - 1;

    if (continua) {
      ultimo.fin = punto.capturedAt;
      ultimo.indiceFin = punto.indice;
      return;
    }

    cortes.push({
      id: punto.id,
      kind: punto.kind,
      detalle: punto.detalle,
      latitude: punto.latitude,
      longitude: punto.longitude,
      inicio: punto.capturedAt,
      fin: punto.capturedAt,
      indiceInicio: punto.indice,
      indiceFin: punto.indice,
    });
  });

  return cortes;
};

const duracion = (inicio, fin) =>
  formatearMinutos(moment(fin).diff(moment(inicio), "minutes"));

const RutaGestor = () => {
  const { selectedGestor, selectedFecha } = useParams();
  const [puntos, setPuntos] = useState([]);
  const [nombreGestor, setNombreGestor] = useState("");
  const [rolGestor, setRolGestor] = useState("");
  const [consultado, setConsultado] = useState(false);

  // El seguimiento en vivo solo tiene sentido en la jornada de hoy: de un dia cerrado no
  // va a llegar nada nuevo.
  const esHoy = selectedFecha === ahoraEnNicaragua().format("YYYY-MM-DD");

  const [enVivo, setEnVivo] = useState(esHoy);
  const [centrado, setCentrado] = useState(0);
  const idUltimaPosicion = useRef(null);

  const { setIsLoading, setIsUnautorized } = useContext(DataContext);
  const token = getToken();

  // El refresco automatico no levanta la pantalla de carga: taparia el mapa cada minuto
  // sin que el usuario haya pedido nada.
  const consultarPuntos = async (silencioso) => {
    if (!silencioso) {
      setIsLoading(true);
    }

    const result = await getRouteAsync(token, selectedGestor, selectedFecha);

    if (!silencioso) {
      setIsLoading(false);
    }

    if (!result.statusResponse) {
      if (result.error === "Unauthorized") {
        setIsUnautorized(true);
        return;
      }
      // Un fallo de red durante el seguimiento no se anuncia: el siguiente intento entra
      // en un minuto y una alerta por cada uno seria insoportable.
      if (!silencioso) {
        simpleMessage(result.error, "error");
      }
      return;
    }

    const nuevos = result.data.result;
    const ultimo = ultimoConocido(nuevos);
    const id = ultimo === null ? null : ultimo.id;

    // El mapa sigue al gestor solo cuando de verdad llego una posicion nueva; recentrar en
    // cada consulta arrastraria la vista aunque nada haya cambiado.
    if (id !== idUltimaPosicion.current) {
      idUltimaPosicion.current = id;
      setCentrado((valor) => valor + 1);
    }

    setPuntos(nuevos);
    setConsultado(true);
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);

      const personal = await getUsuariosCampoAsync(token, null);
      if (personal.statusResponse) {
        const gestor = personal.data.result.find(
          (g) => g.id === Number(selectedGestor)
        );
        setNombreGestor(gestor?.fullName ?? "");
        setRolGestor(gestor?.rolName ?? "");
      }

      await consultarPuntos(false);
    })();
  }, []);

  useEffect(() => {
    if (!enVivo) {
      return undefined;
    }

    const temporizador = setInterval(() => consultarPuntos(true), REFRESCO_MS);
    return () => clearInterval(temporizador);
  }, [enVivo]);

  const conIndice = puntos.map((p, indice) => ({ ...p, indice }));

  const cortes = agruparCortes(conIndice);

  // El trayecto se dibuja como una linea y no como marcadores: una jornada deja cientos
  // de puntos y otros tantos marcadores harian inusable el mapa. Las incidencias quedan
  // fuera del trazo: no son lugares por donde paso, sino momentos sin registro.
  const trazo = conIndice
    .filter((p) => !esIncidencia(p) && p.latitude !== null)
    .map((p) => [p.latitude, p.longitude]);

  const eventos = conIndice.filter(
    (p) => p.kind !== RECORRIDO && !esIncidencia(p) && p.latitude !== null
  );

  // Tramos punteados que unen lo ultimo registrado antes del corte con lo primero
  // despues, para que la interrupcion se vea como un hueco en la ruta y no como un salto
  // inexplicable de la linea.
  const tramosCortados = cortes
    .map((corte) => {
      const antes = conIndice
        .filter((p) => p.indice < corte.indiceInicio && !esIncidencia(p) && p.latitude !== null)
        .pop();
      const despues = conIndice.find(
        (p) => p.indice > corte.indiceFin && !esIncidencia(p) && p.latitude !== null
      );

      if (!antes || !despues) {
        return null;
      }
      return [
        [antes.latitude, antes.longitude],
        [despues.latitude, despues.longitude],
      ];
    })
    .filter(Boolean);

  const minutosSinRegistro = cortes.reduce(
    (total, c) => total + moment(c.fin).diff(moment(c.inicio), "minutes"),
    0
  );

  // Donde esta el gestor: el ultimo punto con coordenadas, sea de recorrido, un hito o una
  // incidencia. En una incidencia es la ultima posicion que el telefono alcanzo a mandar
  // antes de quedarse sin registrar, que es lo mas cercano a donde esta.
  const actual = ultimoConocido(conIndice);
  const minutosSenal = actual === null ? null : minutosDesde(actual.capturedAt);
  const senal = estadoSenal(minutosSenal === null ? Infinity : minutosSenal);
  const actualEsCorte = actual !== null && esIncidencia(actual);

  const posicionActual =
    actual === null ? null : [actual.latitude, actual.longitude];

  // En un dia pasado el pin no dice donde esta el gestor sino donde cerro la jornada, y el
  // color no debe insinuar otra cosa.
  const colorGestor = actualEsCorte
    ? "#d32f2f"
    : esHoy
    ? senal.color
    : "#1769aa";

  const inicioJornada = conIndice.find(
    (p) => !esIncidencia(p) && p.latitude !== null
  );

  // El pin del gestor ya trae el detalle del punto sobre el que cae, asi que ese hito no se
  // dibuja aparte para no quedar un marcador debajo del otro.
  const eventosDibujados = eventos.filter(
    (e) => actual === null || e.id !== actual.id
  );

  const centro =
    posicionActual !== null
      ? posicionActual
      : isEmpty(trazo)
      ? CENTRO_POR_DEFECTO
      : trazo[0];

  return (
    <div>
      <Container maxWidth="xl">
        <Paper style={{ padding: 20 }}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            spacing={2}
          >
            <Typography color={"#00a152"} variant="h4" fontWeight={"bold"}>
              <FontAwesomeIcon icon={faMapLocationDot} />
            </Typography>
            <Typography color={"#00a152"} variant="h4" fontWeight={"bold"}>
              Recorrido del Gestor
            </Typography>
          </Stack>

          <Typography
            variant="h6"
            textAlign={"center"}
            style={{ color: "#1769aa" }}
          >
            {nombreGestor}
          </Typography>
          <Typography variant="subtitle1" textAlign={"center"}>
            {rolGestor ? `${rolGestor} · ` : ""}
            {moment(selectedFecha).format("DD/MM/YYYY")}
          </Typography>

          <hr />

          {/* En la jornada de hoy un dia todavia vacio no es un reporte sin datos: el
          gestor puede no haber salido aun, y cerrar la pantalla con un aviso apagaria el
          seguimiento justo cuando se esta esperando la primera señal. */}
          {consultado && isEmpty(puntos) && !esHoy ? (
            <NoData />
          ) : (
            <>
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                marginTop={2}
                marginBottom={2}
                flexWrap="wrap"
              >
                {actual === null ? (
                  <Chip
                    icon={<FontAwesomeIcon icon={faSatelliteDish} />}
                    label="Sin registros todavía"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    icon={<FontAwesomeIcon icon={faSatelliteDish} />}
                    label={
                      esHoy
                        ? `${
                            actualEsCorte ? "Sin señal desde" : "Visto"
                          } ${moment(actual.capturedAt).format(
                            "hh:mm A"
                          )} · ${haceCuanto(minutosSenal)}`
                        : `Última posición ${moment(actual.capturedAt).format(
                            "hh:mm A"
                          )}`
                    }
                    color={
                      esHoy ? (actualEsCorte ? "error" : senal.chip) : "default"
                    }
                  />
                )}

                {esHoy &&
                actual !== null &&
                actual.batteryLevel !== null &&
                actual.batteryLevel !== undefined &&
                actual.batteryLevel <= 20 ? (
                  <Chip
                    label={`Batería ${actual.batteryLevel}%`}
                    color="warning"
                    variant="outlined"
                  />
                ) : null}

                <Chip
                  label={`${trazo.length} puntos de recorrido`}
                  variant="outlined"
                />
                {Object.entries(EVENTOS).map(([kind, info]) => {
                  const cantidad = eventos.filter(
                    (e) => e.kind === Number(kind)
                  ).length;
                  return cantidad === 0 ? null : (
                    <Chip
                      key={kind}
                      label={`${cantidad} ${info.etiqueta}`}
                      color={info.chip}
                      variant="outlined"
                    />
                  );
                })}

                {isEmpty(cortes) ? null : (
                  <Chip
                    icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                    label={`${cortes.length} ${
                      cortes.length === 1 ? "corte" : "cortes"
                    } · ${formatearMinutos(minutosSinRegistro)} sin ubicación`}
                    color="error"
                  />
                )}
              </Stack>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                alignItems="center"
                marginBottom={2}
                flexWrap="wrap"
              >
                {esHoy ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={enVivo}
                        onChange={(e) => setEnVivo(e.target.checked)}
                        color="success"
                      />
                    }
                    label="Seguimiento en vivo"
                  />
                ) : null}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FontAwesomeIcon icon={faLocationCrosshairs} />}
                  disabled={posicionActual === null}
                  onClick={() => setCentrado((valor) => valor + 1)}
                  style={
                    posicionActual === null
                      ? { borderRadius: 20 }
                      : {
                          borderRadius: 20,
                          color: "#1769aa",
                          borderColor: "#1769aa",
                        }
                  }
                >
                  Centrar en el gestor
                </Button>
              </Stack>

              <MapContainer
                center={centro}
                zoom={14}
                style={{ height: "65vh", width: "100%", borderRadius: 15 }}
                scrollWheelZoom
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <CentrarEn posicion={posicionActual} senal={centrado} />

                {trazo.length > 1 ? (
                  <Polyline positions={trazo} color="#1769aa" weight={4} />
                ) : null}

                {tramosCortados.map((tramo, i) => (
                  <Polyline
                    key={`corte-${i}`}
                    positions={tramo}
                    color="#d32f2f"
                    weight={3}
                    dashArray="8 10"
                  />
                ))}

                {cortes
                  .filter((corte) => corte.latitude !== null)
                  .map((corte) => (
                    <Marker
                      key={`inc-${corte.id}`}
                      position={[corte.latitude, corte.longitude]}
                      icon={iconoAlerta()}
                    >
                      <Popup>
                        <strong style={{ color: "#d32f2f" }}>
                          {corte.detalle || "Seguimiento interrumpido"}
                        </strong>
                        <br />
                        {moment(corte.inicio).format("hh:mm A")} a{" "}
                        {moment(corte.fin).format("hh:mm A")} (
                        {duracion(corte.inicio, corte.fin)})
                        <br />
                        <small>Última posición conocida</small>
                      </Popup>
                    </Marker>
                  ))}

                {inicioJornada === undefined ||
                (actual !== null && inicioJornada.id === actual.id) ? null : (
                  <Marker
                    position={[inicioJornada.latitude, inicioJornada.longitude]}
                    icon={iconoInicio()}
                  >
                    <Popup>
                      <strong>Inicio de la jornada</strong>
                      <br />
                      {moment(inicioJornada.capturedAt).format("hh:mm A")}
                    </Popup>
                  </Marker>
                )}

                {eventosDibujados.map((evento) => (
                  <Marker
                    key={evento.id}
                    position={[evento.latitude, evento.longitude]}
                    icon={icono(EVENTOS[evento.kind]?.color ?? "#757575")}
                  >
                    <Popup>
                      <strong>
                        {moment(evento.capturedAt).format("hh:mm A")}
                      </strong>
                      <br />
                      {evento.detalle ||
                        EVENTOS[evento.kind]?.etiqueta ||
                        "Evento"}
                      <br />
                      <small>Precisión: {Math.round(evento.accuracy)} m</small>
                    </Popup>
                  </Marker>
                ))}

                {/* Va al final y con z-index propio: es lo que el dueño abre el reporte a
                buscar, y no puede quedar tapado por un hito ni por la linea del trayecto. */}
                {posicionActual === null ? null : (
                  <Marker
                    position={posicionActual}
                    icon={iconoGestor(
                      colorGestor,
                      esHoy && senal.viva && !actualEsCorte
                    )}
                    zIndexOffset={1000}
                  >
                    <Popup>
                      <strong style={{ color: colorGestor }}>
                        {esHoy
                          ? actualEsCorte
                            ? "Última posición conocida"
                            : "Aquí está el gestor"
                          : "Última posición del día"}
                      </strong>
                      <br />
                      {nombreGestor}
                      <br />
                      {moment(actual.capturedAt).format("hh:mm A")}
                      {esHoy ? ` · ${haceCuanto(minutosSenal)}` : ""}
                      {actual.detalle ? (
                        <>
                          <br />
                          {actual.detalle}
                        </>
                      ) : null}
                      <br />
                      <small>
                        {actual.accuracy > 0
                          ? `Precisión: ${Math.round(actual.accuracy)} m`
                          : "Sin precisión reportada"}
                        {actual.batteryLevel === null ||
                        actual.batteryLevel === undefined
                          ? ""
                          : ` · Batería: ${actual.batteryLevel}%`}
                      </small>
                    </Popup>
                  </Marker>
                )}
              </MapContainer>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
};

export default RutaGestor;
